import React, { useContext } from "react";
import { uniqBy } from "lodash";
import { useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import {
  GetWorkflowQuery,
  useInsertWorkflowStepMutation,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { spacing } from "../../stitches.config";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { FlexColumn } from "../Common/__styles__/Layout";
import { InputRow } from "../Common/__styles__/Modal";
import { CommonModal } from "../Common/CommonModal";
import Modal from "../Common/Modal";
import Select from "../Inputs/Select";
import { ReactHookFormTextInput } from "../Inputs/Text";

type FormData = {
  workflowStepDefinitionId: string;
  addAtOrderIndex: number;
  name: string;
  workflowId: string;
};

type InsertWorkflowStepModalProps = {
  addAtOrderIndex: number;
  startingName: string;
  workflowStepDefinition: NonNullable<
    GetWorkflowQuery["workflow"]
  >["workflowSteps"][number]["workflowStepDefinition"];
  workflow: NonNullable<GetWorkflowQuery["workflow"]>;
  onCancel: () => void;
  onSuccess: () => void;
};

export const InsertWorkflowStepModal = ({
  addAtOrderIndex,
  startingName,
  workflowStepDefinition,
  workflow,
  onSuccess,
  onCancel,
}: InsertWorkflowStepModalProps) => {
  const { addErrorToast, addSuccessToast } = useStatusToasts();
  const { user, admin } = useContext(AuthContext);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      name: startingName,
      workflowStepDefinitionId: workflowStepDefinition.id,
      addAtOrderIndex,
      workflowId: workflow.id,
    },
  });

  const [insertWorkflowStepMutation, { loading }] =
    useInsertWorkflowStepMutation({
      onCompleted: () => {
        addSuccessToast("Added workflow step successfully");
        onSuccess();
        track("Workflow step inserted", {
          workflowId: workflow.id,
          workflowDefinitionId: workflow.workflowDefinition.id,
          workflowDefinitionName: workflow.workflowDefinition.name,
          userId: user?.id ?? admin?.id,
          userEmail: user?.email ?? admin?.email,
        });
      },
      onError: () => {
        addErrorToast(
          `There was an issue adding your workflow step. Please try again or contact support@withforerunner.com`,
        );
      },
    });

  const onSubmit = async (formData: FormData) => {
    await insertWorkflowStepMutation({
      variables: {
        data: {
          ...formData,
        },
      },
    });
  };

  const submissionTypeOptions = uniqBy(
    workflow.workflowSteps,
    step => step.workflowStepDefinition.submissionType.id,
  ).map(step => ({
    label: step.workflowStepDefinition.submissionType.name,
    value: step.workflowStepDefinition.submissionType.id,
  }));

  const content = (
    <FlexColumn style={{ gap: spacing.m.value }}>
      <InputRow>
        <ReactHookFormTextInput
          label="Name"
          {...register("name", { required: "Name is required" })}
          required
          error={errors.name?.message}
          data-testid="workflow-step-name-input"
        />
      </InputRow>
      <InputRow>
        <Select
          label="Submission type"
          name="submissionTypeId"
          options={submissionTypeOptions}
          value={workflowStepDefinition.submissionType.id}
          onChange={() => {}}
          disabled={true}
          required
        />
      </InputRow>
    </FlexColumn>
  );

  return (
    <CommonModal
      title="Add step"
      subtitle={workflowStepDefinition.name}
      content={content}
      onSubmit={handleSubmit(onSubmit)}
      onCancel={onCancel}
      loading={loading}
      disabled={loading}
      primaryButtonText="Save"
      size="small"
      dataTestId="add-workflow-step-modal"
    />
  );
};

export const useInsertWorkflowStepModal = ({
  startingName,
  workflowStepDefinition,
  workflow,
  addAtOrderIndex,
}: {
  startingName: string;
  workflowStepDefinition: NonNullable<
    GetWorkflowQuery["workflow"]
  >["workflowSteps"][number]["workflowStepDefinition"];
  workflow: NonNullable<GetWorkflowQuery["workflow"]>;
  addAtOrderIndex: number;
}) => {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <InsertWorkflowStepModal
          addAtOrderIndex={addAtOrderIndex}
          startingName={startingName}
          workflowStepDefinition={workflowStepDefinition}
          workflow={workflow}
          onCancel={hideModal}
          onSuccess={hideModal}
        />
      </Modal>
    ),
    [startingName, workflowStepDefinition, workflow, addAtOrderIndex],
  );

  return [showModal, hideModal] as const;
};
